import { NgClass, SlicePipe } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    output,
    Renderer2,
    signal,
    viewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatFabButton, MatIconAnchor, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { NgxExtendedPdfViewerModule, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';

import { BaasAppState } from '@lvflanders/bar';
import { BaasRemark, BarI18nPipe } from '@lvflanders/bar-ng-ui';
import { DidoCoreStateService } from '@lvflanders/digitaaldossier-ng';

import { DidoDocumentenService } from '../../services/dido-documenten.service';
import { DocumentModel } from '../../types/dido-document-model.type';
import pdfViewerTranslations from './pdf-viewer-translations';

const IMG_EXTENSIES = ['.tiff', '.png', '.jpg', '.jpeg', '.gif'];
const CONVERTEERBARE_EXTENSIES = ['.xls', '.xlsx', '.doc', '.docx', '.rtf', '.txt', '.xml'];
const TXT_EXTENSIES = ['.txt'];

@Component({
    selector: 'dido-documenten-gallery',
    templateUrl: './dido-documenten-gallery.html',
    styleUrls: ['./dido-documenten-gallery.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIconButton, MatTooltip, MatIcon, MatIconAnchor, MatFabButton, NgClass, BaasRemark, NgxExtendedPdfViewerModule, SlicePipe, BarI18nPipe]
})
export class DidoDocumentenGallery implements AfterViewInit {
    protected readonly data = inject(MAT_DIALOG_DATA);
    readonly #dialogRef = inject(MatDialogRef<DidoDocumentenGallery>);
    readonly #appState = inject(BaasAppState);
    readonly #didoDocService = inject(DidoDocumentenService);
    readonly #didoCoreStateService = inject(DidoCoreStateService);
    readonly #renderer2 = inject(Renderer2);

    readonly msgFrame = viewChild.required<ElementRef>('msgFrame');

    readonly onEdit = output<DocumentModel>();

    protected readonly intern: boolean = false;
    protected readonly readonly = false;
    protected readonly isUploadPreview = false;

    protected readonly selectedItem = signal<DocumentModel>(undefined as unknown as DocumentModel);
    protected readonly pdfSrc = signal<any>(undefined);
    protected readonly sideNavigationOpen = signal<boolean>(false);
    protected readonly isImage = computed(() => IMG_EXTENSIES.includes(this.selectedItem().bestandsextensie.toLowerCase()));
    protected readonly isMail = computed(() => this.selectedItem().bestandsextensie.toLowerCase() == '.msg');
    protected readonly isTxt = computed(() => TXT_EXTENSIES.includes(this.selectedItem().bestandsextensie.toLowerCase()));
    protected readonly isUrlBestand = computed(() => this.selectedItem().bestandsextensie.toLowerCase() == '.url');

    protected readonly showSideNavigation = signal<boolean>(true);
    protected readonly showOriButton = signal<boolean>(true);
    protected readonly downloadUrl = computed(
        () =>
            `/a/dido2/web/api/documenten/download-file?documentnummer=${this.selectedItem().documentnummer}${this.selectedItem().bestandsnaam ? '&filename=' + encodeURIComponent(this.selectedItem().bestandsnaam as string) : ''}`
    );

    readonly #isConverteerbareExtensie = computed(() => CONVERTEERBARE_EXTENSIES.includes(this.selectedItem().bestandsextensie.toLowerCase()));

    #counter = 0;
    readonly #pdfjsUrl = this.#didoCoreStateService.didoWebUrl + 'pdfjs';

    constructor() {
        this.#addScript();
        pdfDefaultOptions.assetsFolder = this.#pdfjsUrl;
        pdfDefaultOptions.workerSrc = () => this.#pdfjsUrl + '/pdf.worker-4.1.889.mjs';

        this.intern = this.#appState.basiscontext.zone == 'I';
        this.selectedItem.set(this.data.selectedDocument);
        this.isUploadPreview = this.data.isUploadPreview;
        if (!this.isUploadPreview) {
            this.readonly = this.data.readonly;
            this.#counter = this.data.documenten ? this.data.documenten.indexOf(this.selectedItem()) : 0;
        }
        if (this.data.showSideNavigation != undefined) {
            this.showSideNavigation.set(!!this.data.showSideNavigation);
        }
        if (this.data.showOriButton != undefined) {
            this.showOriButton.set(!!this.data.showOriButton);
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.updateGallery();
        });
    }

    protected close(): void {
        this.#dialogRef.close();
    }

    protected goToNext(): void {
        this.#counter++;
        if (this.#counter == this.data.documenten.length) this.#counter = 0;
        this.selectedItem.set(this.data.documenten[this.#counter]);
        this.updateGallery();
    }

    protected goToPrev(): void {
        this.#counter--;
        if (this.#counter == -1) this.#counter = this.data.documenten.length - 1;
        this.selectedItem.set(this.data.documenten[this.#counter]);
        this.updateGallery();
    }

    protected toggleSideNavigation() {
        this.sideNavigationOpen.update((sideNavigationOpen) => !sideNavigationOpen);
    }

    protected updateGallery(): void {
        if (this.isImage() || this.isUrlBestand()) {
            if (!this.selectedItem().content && this.selectedItem().documentnummer) {
                this.#didoDocService.getContent(this.selectedItem().documentnummer, this.selectedItem().bestandsnaam, false).subscribe((result) => {
                    this.selectedItem.update((item) => ({ ...item, content: result }));
                    if (this.isUrlBestand()) {
                        const tobase64 = this.#b64_to_utf8(result);
                        const urlPattern = /URL=(https?:\/\/[^\s]+)/;
                        const match = tobase64.match(urlPattern);
                        this.selectedItem.update((item) => ({ ...item, content: match ? match[1] : undefined }));
                    }
                });
            }
        } else if (this.isMail()) {
            if (!this.selectedItem().content && this.selectedItem().documentnummer) {
                this.#didoDocService.getMailPreview(this.selectedItem().documentnummer, this.selectedItem().bestandsnaam).subscribe((result) => {
                    this.msgFrame().nativeElement.innerHTML = this.#b64_to_utf8(result);
                    this.msgFrame()
                        .nativeElement.querySelectorAll('a')
                        .forEach(function (a) {
                            if (a.href.indexOf('mailto:') == -1) {
                                a.setAttribute('target', '_blank');
                            }
                        });
                });
            } else {
                this.#didoDocService.getMailPreviewContent(this.selectedItem().content, this.selectedItem().bestandsnaam).subscribe((result) => {
                    this.msgFrame().nativeElement.innerHTML = this.#b64_to_utf8(result);
                    this.msgFrame()
                        .nativeElement.querySelectorAll('a')
                        .forEach(function (a) {
                            if (a.href.indexOf('mailto:') == -1) {
                                a.setAttribute('target', '_blank');
                            }
                        });
                });
            }
        } else {
            if (!this.selectedItem().content && this.selectedItem().documentnummer) {
                this.#didoDocService
                    .getContent(this.selectedItem().documentnummer, this.selectedItem().bestandsnaam, this.#isConverteerbareExtensie())
                    .subscribe((result) => {
                        this.selectedItem.update((item) => ({ ...item, content: result }));
                        this.pdfSrc.set(result);
                    });
            } else {
                this.pdfSrc.set(this.selectedItem().content);
            }
        }
    }

    protected onEditClicked(): void {
        this.onEdit.emit(this.selectedItem());
    }

    #addScript() {
        const s = this.#renderer2.createElement('script');
        s.type = 'application/l10n';
        s.text = pdfViewerTranslations;
        document.getElementsByTagName('head')[0].appendChild(s);
    }

    #b64_to_utf8(str) {
        return decodeURIComponent(escape(window.atob(str)));
    }
}
